/**
 * Workshop-Lücke E2 (Plan 2026-10-29): Vorjahreslinie der Karte „Anfos je Kanal und Monat". Reine Rechnung, getestet in
 * `vorjahr.test.ts`.
 *
 * Regeln:
 * - Wert = Σ der gewählten Kanalgruppen im Monat m − 12 (volle Vorjahresmonate aus `rows`, die API liefert 24 Monate).
 * - Der laufende Monat vergleicht mit dem Vorjahresmonat bis zum selben Tag (`vorjahr_teilmonat` der API), nie mit dem
 *   vollen Monat.
 * - Monate, deren Vorjahresmonat nicht in der Antwort steht (24-Monats-Ansicht: die ältere Hälfte), haben keinen Wert.
 * - „Anteil %": Anteil der Auswahl an ALLEN Anfos (bzw. am ganzen Erwarteten Wert) des Vorjahresmonats — wie die Balken.
 * - „Bisher bezahlt": keine Linie — ein Vorjahresmonat hatte zwölf Monate mehr Zeit zum Zahlen.
 */
import type { KanalGruppeKey, VerlaufMonat, VerlaufZeile, VorjahrTeilmonat } from '../../../helpers/auswertungenData';

export type VorjahrKennzahl = 'anfos' | 'wert' | 'bezahlt';

/** `2026-10` → `2025-10`. */
export function monatVorjahr(m: string): string {
  return `${Number(m.slice(0, 4)) - 1}${m.slice(4)}`;
}

export interface VorjahrPunkt {
  /** Vorjahresmonat `YYYY-MM`. */
  month: string;
  /** Teilmonat: letzter gezählter Tag im Vorjahr (`YYYY-MM-DD`), sonst null. */
  bis: string | null;
  /** Σ der Auswahl (bzw. Anteil in % bei `anteil`); null = kein Vergleich möglich. */
  auswahl: number | null;
  /** Je Gruppe (Linienansicht; bei `anteil` Anteil der Gruppe in %). */
  je: Record<string, number>;
  /** Alle Gruppen (Nenner des Anteils). */
  alle: number;
}

export interface VorjahrEingabe {
  months: VerlaufMonat[];
  rows: VerlaufZeile[];
  teilmonat?: VorjahrTeilmonat;
  gruppeVon: ReadonlyMap<string, KanalGruppeKey>;
  /** Gewählte Gruppen. */
  auswahl: readonly string[];
  kennzahl: VorjahrKennzahl;
  anteil: boolean;
}

/**
 * Vorjahreswerte je angezeigtem Monat. `months` = die Monate der Ansicht (12 oder 24); die Vorjahresmonate werden in
 * `rows` gesucht, die alle 24 Monate der Antwort enthalten.
 */
export function vorjahrJeMonat(e: VorjahrEingabe, monateDerAntwort: readonly string[]): Map<string, VorjahrPunkt> {
  const out = new Map<string, VorjahrPunkt>();
  if (e.kennzahl === 'bezahlt') return out;
  const vorhanden = new Set(monateDerAntwort);
  const wert = (r: VerlaufZeile) => (e.kennzahl === 'anfos' ? r.anfos : r.value_sum);
  for (const m of e.months) {
    const vm = monatVorjahr(m.month);
    let zeilen: VerlaufZeile[] | null = null;
    let bis: string | null = null;
    if (m.is_partial) {
      if (e.teilmonat && e.teilmonat.zu_month === m.month && e.teilmonat.month === vm) {
        zeilen = e.teilmonat.rows;
        bis = e.teilmonat.until;
      }
    } else if (vorhanden.has(vm)) {
      zeilen = e.rows.filter((r) => r.month === vm);
    }
    if (zeilen === null) continue;
    const je: Record<string, number> = {};
    let alle = 0;
    for (const r of zeilen) {
      const g = e.gruppeVon.get(r.channel) ?? 'other';
      je[g] = (je[g] ?? 0) + wert(r);
      alle += wert(r);
    }
    const summe = e.auswahl.reduce((s, g) => s + (je[g] ?? 0), 0);
    const jeAnzeige: Record<string, number> = {};
    for (const g of e.auswahl) jeAnzeige[g] = e.anteil ? (alle > 0 ? ((je[g] ?? 0) / alle) * 100 : 0) : (je[g] ?? 0);
    out.set(m.month, {
      month: vm,
      bis,
      auswahl: e.anteil ? (alle > 0 ? (summe / alle) * 100 : null) : summe,
      je: jeAnzeige,
      alle,
    });
  }
  return out;
}
